// Utility to merge multiple PDFs/images into a single PDF in the browser
// Uses pdf-lib for PDF operations; images are embedded as pages.
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { getBytes, ref as storageRef } from 'firebase/storage';
import { storage } from '../services/firebaseConfig';

// Robust fetch for bytes that prefers Firebase Storage SDK (works with gs://, https URLs)
const fetchArrayBuffer = async (url) => {
  // 1) Try Firebase Storage SDK
  try {
    const ref = storageRef(storage, url);
    const bytes = await getBytes(ref);
    // Return Uint8Array directly; pdf-lib accepts Uint8Array and avoids issues with underlying ArrayBuffer offsets
    return bytes;
  } catch (_) {
    // ignore and try fetch
  }
  // 2) Fallback to fetch
  const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
  if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
  return await res.arrayBuffer();
};

export async function mergeDocumentsToPdf(documents = [], { title = 'Dispatch Documents' } = {}) {
  const outPdf = await PDFDocument.create();

  // Optional cover page
  const page = outPdf.addPage([595.28, 841.89]); // A4
  const font = await outPdf.embedFont(StandardFonts.Helvetica);
  const now = new Date().toLocaleString();
  page.drawText(title, { x: 50, y: 790, size: 18, font, color: rgb(0, 0.4, 0.4) });
  page.drawText(`Generated at: ${now}`, { x: 50, y: 770, size: 10, font, color: rgb(0.2, 0.2, 0.2) });

  for (const doc of documents) {
    const name = (doc.fileName || doc.title || 'document').toLowerCase();
    const url = doc.url;
    const contentType = (doc.contentType || '').toLowerCase();
    if (!url) continue;

    try {
      const isPdf = name.endsWith('.pdf') || contentType.includes('application/pdf');
      const isPng = name.endsWith('.png') || contentType.includes('image/png');
      const isJpg = name.endsWith('.jpg') || name.endsWith('.jpeg') || contentType.includes('image/jpeg');
      const isWebp = name.endsWith('.webp') || contentType.includes('image/webp');

      if (isPdf) {
        const bytes = await fetchArrayBuffer(url);
        const srcPdf = await PDFDocument.load(bytes);
        const copied = await outPdf.copyPages(srcPdf, srcPdf.getPageIndices());
        copied.forEach((p) => outPdf.addPage(p));
      } else if (isPng || isJpg) {
        const imgBytes = await fetchArrayBuffer(url);
        let img;
        if (isPng) img = await outPdf.embedPng(imgBytes); else img = await outPdf.embedJpg(imgBytes);
        const { width, height } = img;
        const a4w = 595.28, a4h = 841.89, margin = 36;
        const pw = a4w - margin * 2; const ph = a4h - margin * 2;
        const scale = Math.min(pw / width, ph / height);
        const w = width * scale, h = height * scale;
        const p = outPdf.addPage([a4w, a4h]);
        p.drawImage(img, { x: (a4w - w) / 2, y: (a4h - h) / 2, width: w, height: h });
      } else if (isWebp) {
        // Convert WEBP -> PNG via canvas, then embed
        const ab = await fetchArrayBuffer(url);
        const webpBlob = new Blob([ab], { type: 'image/webp' });
        const bitmap = await createImageBitmap(webpBlob);
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width; canvas.height = bitmap.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(bitmap, 0, 0);
        const pngBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
        const pngBytes = await pngBlob.arrayBuffer();
        const img = await outPdf.embedPng(pngBytes);
        const { width, height } = img;
        const a4w = 595.28, a4h = 841.89, margin = 36;
        const pw = a4w - margin * 2; const ph = a4h - margin * 2;
        const scale = Math.min(pw / width, ph / height);
        const w = width * scale, h = height * scale;
        const p = outPdf.addPage([a4w, a4h]);
        p.drawImage(img, { x: (a4w - w) / 2, y: (a4h - h) / 2, width: w, height: h });
      } else {
        // Unsupported type; skip
        // Could add office file preview conversion if needed
      }
    } catch (e) {
      console.error('Merge error for', name, e);
      // Continue with others
    }
  }

  const outBytes = await outPdf.save();
  const blob = new Blob([outBytes], { type: 'application/pdf' });
  return blob;
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
